<template>
	<div class="h-full min-h-0">
		<FullCalendar ref="fullCalendarRef" class="calendar-view" :options="calendarOptions" />

		<AppointmentFormModal
			v-model:show="showFormModal"
			:appointment="selectedAppointment"
			:initial-datetime
			:initial-end-datetime
			:initial-appointment-type
			:initial-all-day
			:initial-duration
			:initial-office-id
			:initial-patient-id
			@saved="reloadAppointments()"
			@deleted="reloadAppointments()"
		/>
	</div>
</template>

<script setup lang="ts">
import type {
	CalendarApi,
	CalendarOptions,
	DateClickInfo,
	DateSelectInfo,
	DatesSetInfo,
	EventClickInfo,
	EventDropInfo,
	EventInput,
	EventResizeDoneInfo
} from "@fullcalendar/vue3"
import type { CalendarPeriodDisplay, CalendarViewType } from "@/components/calendar/CalendarToolbar.vue"
import type { I18nString } from "@/lang/config"
import type { Appointment, AppointmentFormType } from "@/types/appointment"
import FullCalendar from "@fullcalendar/vue3"
import dayGridPlugin from "@fullcalendar/vue3/daygrid"
import interactionPlugin from "@fullcalendar/vue3/interaction"
import listPlugin from "@fullcalendar/vue3/list"
// TODO-NEXT: MVP2 implements locales
import enGbLocale from "@fullcalendar/vue3/locales/en-gb"
import itLocale from "@fullcalendar/vue3/locales/it"
import multiMonthPlugin from "@fullcalendar/vue3/multimonth"
import themePlugin from "@fullcalendar/vue3/themes/forma"
import timeGridPlugin from "@fullcalendar/vue3/timegrid"
import { useDialog, useMessage } from "naive-ui"
import { storeToRefs } from "pinia"
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from "vue"
import { useI18n } from "vue-i18n"
import Api from "@/api"
import AppointmentFormModal from "@/components/calendar/AppointmentFormModal.vue"
import { emitter } from "@/emitter"
import { useAuthStore } from "@/stores/auth"
import { useLocalesStore } from "@/stores/i18n"
import { useOfficesStore } from "@/stores/offices"
import { confirmDialog, getApiErrorMessage } from "@/utils"
import {
	dayCountToAllDayDuration,
	getAllDayEventRange,
	getCalendarAppointmentEventColors,
	getCalendarAppointmentTitle,
	getFirstAvailableDatetimeForDay,
	startOfDayMs
} from "@/utils/calendar"
import dayjs, { dPayload } from "@/utils/dayjs"
import "@fullcalendar/vue3/skeleton.css"
import "@fullcalendar/vue3/themes/forma/theme.css"
import "@fullcalendar/vue3/themes/forma/palettes/blue.css"

const props = withDefaults(
	defineProps<{
		selectedOfficeId?: number | null
		selectedPatientId?: number | null
		viewEditable?: boolean
		confirmEventUpdate?: boolean
		initialView?: CalendarViewType
	}>(),
	{
		viewEditable: false,
		confirmEventUpdate: false,
		initialView: "timeGridWeek"
	}
)

const message = useMessage()
const dialog = useDialog()
const { t } = useI18n()
const authStore = useAuthStore()
const localesStore = useLocalesStore()
const officesStore = useOfficesStore()
const { list: offices } = storeToRefs(officesStore)

const fullCalendarRef = ref<{ getApi: () => CalendarApi } | null>(null)
const loading = ref(false)
const appointments = shallowRef<Appointment[]>([])
const showFormModal = ref(false)
const selectedAppointment = ref<Appointment | null>(null)
const initialDatetime = ref<number | null>(null)
const initialEndDatetime = ref<number | null>(null)
const initialAppointmentType = ref<AppointmentFormType | null>(null)
const initialAllDay = ref<boolean | null>(null)
const initialDuration = ref<number | null>(null)
const initialOfficeId = ref<number | null>(null)
const initialPatientId = ref<number | null>(null)
const calendarView = ref<CalendarViewType>(props.initialView)
const periodAnchor = ref<Date>(new Date())
const periodRange = ref<{ start: Date; end: Date } | null>(null)
const visibleRange = ref<{ from: Date; to: Date } | null>(null)

let abortController: AbortController | null = null
let lastFetchedRange: { from: number; to: number } | null = null

const filteredAppointments = computed(() =>
	appointments.value.filter(appointment => {
		if (props.selectedOfficeId != null && appointment.office?.id !== props.selectedOfficeId) {
			return false
		}

		if (props.selectedPatientId != null && appointment.patient?.id !== props.selectedPatientId) {
			return false
		}

		return true
	})
)

function getInclusivePeriodBounds(start: Date, end: Date) {
	const rangeStart = dayjs(start).startOf("day")
	const rangeEnd = dayjs(end).subtract(1, "millisecond").startOf("day")

	return { rangeStart, rangeEnd }
}

function formatSingleDay(date: dayjs.Dayjs): CalendarPeriodDisplay {
	return {
		mode: "day",
		primary: date.format("D MMMM"),
		secondary: date.format("YYYY")
	}
}

function formatDateRange(rangeStart: dayjs.Dayjs, rangeEnd: dayjs.Dayjs): CalendarPeriodDisplay {
	if (rangeStart.isSame(rangeEnd, "day")) {
		return formatSingleDay(rangeStart)
	}

	if (rangeStart.isSame(rangeEnd, "month") && rangeStart.isSame(rangeEnd, "year")) {
		return {
			mode: "range",
			primary: `${rangeStart.format("D")} – ${rangeEnd.format("D MMMM")}`,
			secondary: rangeEnd.format("YYYY")
		}
	}

	if (rangeStart.isSame(rangeEnd, "year")) {
		return {
			mode: "range",
			primary: `${rangeStart.format("D MMMM")} – ${rangeEnd.format("D MMMM")}`,
			secondary: rangeEnd.format("YYYY")
		}
	}

	return {
		mode: "range",
		primary: `${rangeStart.format("D MMMM YYYY")} – ${rangeEnd.format("D MMMM YYYY")}`,
		secondary: ""
	}
}

function getUniqueAppointmentDays() {
	const locale = localesStore.locale

	return [
		...new Set(
			filteredAppointments.value.map(appointment =>
				dayjs(appointment.datetime, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD")
			)
		)
	]
		.sort()
		.map(date => dayjs(date).locale(locale))
}

const periodDisplay = computed<CalendarPeriodDisplay>(() => {
	const locale = localesStore.locale
	const anchor = dayjs(periodAnchor.value).locale(locale)
	const view = calendarView.value

	if (view === "multiMonthYear") {
		return { mode: "year", year: anchor.format("YYYY") }
	}

	if (view === "dayGridMonth") {
		return { mode: "month", month: anchor.format("MMMM"), year: anchor.format("YYYY") }
	}

	const bounds = periodRange.value
		? getInclusivePeriodBounds(periodRange.value.start, periodRange.value.end)
		: { rangeStart: anchor.startOf("day"), rangeEnd: anchor.startOf("day") }

	const rangeStart = bounds.rangeStart.locale(locale)
	const rangeEnd = bounds.rangeEnd.locale(locale)

	if (view === "timeGridDay") {
		return formatSingleDay(rangeStart)
	}

	if (view === "listWeek") {
		const appointmentDays = getUniqueAppointmentDays()

		if (appointmentDays.length === 1) {
			return formatSingleDay(appointmentDays[0]!)
		}
	}

	return formatDateRange(rangeStart, rangeEnd)
})

const calendarEvents = computed<EventInput[]>(() =>
	filteredAppointments.value.map(appointment => {
		const start = dayjs(appointment.datetime, "YYYY-MM-DD HH:mm:ss")
		const durationMinutes = appointment.duration ?? authStore.avgVisitDuration
		const isAllDay = appointment.all_day
		const { color, contrastColor } = getCalendarAppointmentEventColors(appointment, offices.value)
		const title = getCalendarAppointmentTitle(
			appointment,
			appointment.draft ? t("appointments.common.draft") : undefined
		)

		if (isAllDay) {
			const range = getAllDayEventRange({ datetime: appointment.datetime, duration: durationMinutes })

			return {
				id: String(appointment.id),
				title,
				start: range.start,
				end: range.end,
				allDay: true,
				color: "var(--primary-300-color)",
				contrastColor: "var(--primary-700-color)",
				classNames: ["calendar-all-day-event"],
				extendedProps: {
					appointment
				}
			}
		}

		return {
			id: String(appointment.id),
			title,
			start: start.toISOString(),
			end: start.add(durationMinutes, "minute").toISOString(),
			color,
			contrastColor,
			extendedProps: {
				appointment
			}
		}
	})
)

const hasAllDayEvents = computed(() => filteredAppointments.value.some(appointment => appointment.all_day))

const locales = {
	it: itLocale,
	en: enGbLocale
} as const

const locale = computed(() => locales[localesStore.locale as keyof typeof locales])

const calendarOptions = computed<CalendarOptions>(() => ({
	plugins: [themePlugin, multiMonthPlugin, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
	initialView: calendarView.value,
	headerToolbar: false,
	locale: locale.value,
	firstDay: 1,
	multiMonthMaxColumns: 3,
	slotMinTime: "07:00:00",
	slotMaxTime: "21:00:00",
	allDaySlot: hasAllDayEvents.value,
	nowIndicator: true,
	editable: props.viewEditable,
	eventDurationEditable: props.viewEditable,
	selectable: true,
	selectMinDistance: 5,
	height: "100%",
	events: calendarEvents.value,
	viewClass: "border-none! rounded-none!",
	datesSet: handleDatesSet,
	dateClick: handleDateClick,
	select: handleDateSelect,
	eventClick: handleEventClick,
	eventDrop: handleEventDrop,
	eventResize: handleEventResize
}))

function getCalendarApi() {
	return fullCalendarRef.value?.getApi()
}

function navigateCalendar(action: "prev" | "next" | "today") {
	getCalendarApi()?.[action]()
}

function changeCalendarView(view: CalendarViewType) {
	getCalendarApi()?.changeView(view)
}

function handleDatesSet(arg: DatesSetInfo) {
	calendarView.value = arg.view.type as CalendarViewType
	periodAnchor.value = arg.view.calendar.getDate()
	periodRange.value = { start: arg.start, end: arg.end }
	loadAppointments(arg.start, arg.end)
}

function loadAppointments(from: Date, to: Date, force = false) {
	const fromMs = from.getTime()
	const toMs = to.getTime()

	if (!force && lastFetchedRange?.from === fromMs && lastFetchedRange?.to === toMs) {
		return
	}

	abortController?.abort()
	abortController = new AbortController()
	lastFetchedRange = { from: fromMs, to: toMs }
	visibleRange.value = { from, to }
	loading.value = true

	Api.common.appointments
		.list(
			{
				from,
				to
			},
			abortController.signal
		)
		.then(res => {
			appointments.value = res.data ?? []
		})
		.catch(err => {
			if (abortController?.signal.aborted) return

			message.error(getApiErrorMessage(err) || t<I18nString>(`common.misc.generic_error`))
		})
		.finally(() => {
			if (!abortController?.signal.aborted) {
				loading.value = false
			}
		})
}

function reloadAppointments() {
	if (!visibleRange.value) return

	loadAppointments(visibleRange.value.from, visibleRange.value.to, true)
}

function resetCreateModalInitials() {
	initialDatetime.value = null
	initialEndDatetime.value = null
	initialAppointmentType.value = null
	initialAllDay.value = null
	initialDuration.value = null
	initialOfficeId.value = null
	initialPatientId.value = null
}

function openCreateModal(datetime?: number) {
	selectedAppointment.value = null
	resetCreateModalInitials()
	initialDatetime.value = datetime ?? Date.now()
	initialOfficeId.value = props.selectedOfficeId ?? null
	initialPatientId.value = props.selectedPatientId ?? null
	showFormModal.value = true
}

function getInclusiveAllDayEnd(start: Date, endExclusive: Date) {
	const startDay = dayjs(start).startOf("day")
	const inclusiveEndDay = dayjs(endExclusive).startOf("day").subtract(1, "day")

	if (inclusiveEndDay.isBefore(startDay, "day")) {
		return startDay.valueOf()
	}

	return inclusiveEndDay.valueOf()
}

function openCreateModalFromSelection(info: DateSelectInfo) {
	selectedAppointment.value = null
	resetCreateModalInitials()
	initialOfficeId.value = props.selectedOfficeId ?? null
	initialPatientId.value = props.selectedPatientId ?? null

	if (info.allDay) {
		initialAppointmentType.value = "other"
		initialAllDay.value = true
		initialDatetime.value = startOfDayMs(info.start)
		initialEndDatetime.value = getInclusiveAllDayEnd(info.start, info.end)
	} else {
		const durationMinutes = dayjs(info.end).diff(dayjs(info.start), "minute")

		if (durationMinutes > 0) {
			initialAppointmentType.value = "visit"
			initialDatetime.value = info.start.getTime()
			initialDuration.value = durationMinutes
		} else {
			initialAppointmentType.value = "other"
			initialAllDay.value = false
			initialDatetime.value = info.start.getTime()
			initialEndDatetime.value = info.end.getTime()
		}
	}

	showFormModal.value = true
	getCalendarApi()?.unselect()
}

function isMonthCalendarView(viewType: string) {
	return viewType === "dayGridMonth" || viewType === "multiMonthYear"
}

function getCreateModalDatetimeFromDateClick(arg: DateClickInfo) {
	if (!isMonthCalendarView(arg.view.type)) {
		return arg.date.getTime()
	}

	return getFirstAvailableDatetimeForDay(arg.date, filteredAppointments.value, authStore.avgVisitDuration)
}

function handleDateClick(arg: DateClickInfo) {
	openCreateModal(getCreateModalDatetimeFromDateClick(arg))
}

function handleDateSelect(info: DateSelectInfo) {
	openCreateModalFromSelection(info)
}

function handleEventClick(arg: EventClickInfo) {
	const appointment = arg.event.extendedProps.appointment as Appointment | undefined
	if (!appointment) return

	selectedAppointment.value = appointment
	resetCreateModalInitials()
	showFormModal.value = true
}

function getEventDurationMinutes(start: Date, end: Date | null, fallback = authStore.avgVisitDuration) {
	if (!end) return fallback

	const minutes = dayjs(end).diff(dayjs(start), "minute")
	return minutes > 0 ? minutes : fallback
}

function getAppointmentRelationIds(appointment: Appointment) {
	return {
		...(appointment.patient?.id != null ? { patient_id: appointment.patient.id } : {}),
		...(appointment.office?.id != null ? { office_id: appointment.office.id } : {})
	}
}

function buildUpdatePayload(appointment: Appointment, start: Date, end: Date | null, allDay = false) {
	const relationIds = getAppointmentRelationIds(appointment)

	if (allDay && end) {
		const startDay = dayjs(start).startOf("day")
		// FullCalendar's all-day end is exclusive, so the day count is the plain difference.
		const dayCount = dayjs(end).startOf("day").diff(startDay, "day")

		return {
			type: appointment.type,
			all_day: true,
			datetime: dPayload(startDay.valueOf(), "datetime"),
			title: appointment.title,
			...relationIds,
			draft: appointment.draft,
			duration: dayCountToAllDayDuration(dayCount)
		}
	}

	return {
		type: appointment.type,
		all_day: allDay,
		datetime: dPayload(start, "datetime"),
		title: appointment.title,
		...relationIds,
		draft: appointment.draft,
		duration: getEventDurationMinutes(start, end, appointment.duration ?? authStore.avgVisitDuration)
	}
}

function persistAppointmentFromEvent(arg: EventDropInfo | EventResizeDoneInfo, successMessage: I18nString) {
	const appointment = arg.event.extendedProps.appointment as Appointment | undefined
	if (!appointment || !arg.event.start) {
		arg.revert()
		return
	}

	const payload = buildUpdatePayload(appointment, arg.event.start, arg.event.end, arg.event.allDay)

	Api.common.appointments
		.update(appointment.id, payload)
		.then(res => {
			const updated: Appointment = {
				...res.data,
				duration: payload.duration
			}

			appointments.value = appointments.value.map(item => (item.id === updated.id ? updated : item))
			arg.event.setExtendedProp("appointment", updated)
			message.success(t<I18nString>(successMessage))
		})
		.catch(err => {
			arg.revert()
			message.error(getApiErrorMessage(err) || t<I18nString>(`common.misc.generic_error`))
		})
}

interface EventUpdateConfirm {
	title: I18nString
	content: I18nString
}

function confirmAndPersistAppointmentFromEvent(
	arg: EventDropInfo | EventResizeDoneInfo,
	successMessage: I18nString,
	confirm: EventUpdateConfirm
) {
	if (!props.confirmEventUpdate) {
		persistAppointmentFromEvent(arg, successMessage)
		return
	}

	confirmDialog(dialog, {
		title: t<I18nString>(confirm.title),
		content: t<I18nString>(confirm.content),
		positiveText: t<I18nString>(`common.misc.confirm`),
		negativeText: t<I18nString>(`common.misc.cancel`),
		success: () => {
			persistAppointmentFromEvent(arg, successMessage)
		},
		cancel: () => {
			arg.revert()
		}
	})
}

function handleEventDrop(arg: EventDropInfo) {
	confirmAndPersistAppointmentFromEvent(arg, `appointments.form.messages.moved`, {
		title: `appointments.common.move_appointment`,
		content: `appointments.common.move_confirm`
	})
}

function handleEventResize(arg: EventResizeDoneInfo) {
	confirmAndPersistAppointmentFromEvent(arg, `appointments.form.messages.updated`, {
		title: `appointments.common.update_duration`,
		content: `appointments.common.update_duration_confirm`
	})
}

watch(
	() => localesStore.locale,
	() => {
		getCalendarApi()?.setOption("locale", localesStore.locale === "it" ? itLocale : enGbLocale)
	}
)

// Refresh when an appointment is saved elsewhere (e.g. the new-appointment modal opened from the search dialog).
onMounted(() => {
	emitter.on("appointment:saved", reloadAppointments)
})

onUnmounted(() => {
	emitter.off("appointment:saved", reloadAppointments)
})

defineExpose({
	calendarView,
	periodDisplay,
	loading,
	navigateCalendar,
	changeCalendarView,
	reloadAppointments
})
</script>

<style lang="scss">
.calendar-view {
	--fc-forma-primary: var(--primary-400-color);
	--fc-forma-primary-over: var(--primary-700-color);
	--fc-forma-primary-down: var(--primary-500-color);
	--fc-forma-event: var(--primary-500-color);
	--fc-forma-background-event: var(--fc-forma-primary);
	--fc-forma-highlight: var(--primary-100-color);
}
</style>
